import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { leadApi } from "../../pages/services/leadService";
import LeadForm from "./LeadForm";
import {
  ChannelBadge, FollowUpInput, MESSAGE_KIND, StatusPill, errorText, fmtDateTime, fmtDue, fromLocalInput, isOverdue, refreshUntilDone, sourceLabel, toLocalInput, useLeadMeta,
} from "./leadUtils";
import "../../styles/Leads.css";

const FINAL = ["registered", "not_qualified", "not_interested"];
const KIND = MESSAGE_KIND;


const MetaCard = ({ meta }) => {
  const rows = [
    ["Platform", meta.platform === "ig" ? "Instagram" : meta.platform === "fb" ? "Facebook" : meta.platform || "—"],
    ["Campaign", meta.campaignName || meta.campaignId || "—"],
    ["Ad set", meta.adsetName || meta.adsetId || "—"],
    ["Ad", meta.adName || meta.adId || "—"],
    ["Organic", meta.isOrganic ? "Yes" : "No (paid)"],
    ["Form submitted", fmtDateTime(meta.createdTime)],
    ...(meta.phoneRaw ? [["Phone as typed", meta.phoneRaw]] : []),
  ];
  const answers = Object.entries(meta.answers || {});
  const label = (k) => k.replace(/[_?]+/g, " ").trim().replace(/^./, (c) => c.toUpperCase());
  return (
    <div className="ld-card">
      <h3 className="ld-card-title">Meta Lead Ads{meta.isTest ? <span className="ld-chip" style={{ marginLeft: 8 }}>Test lead?</span> : null}</h3>
      <dl className="ld-kv">{rows.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}</dl>
      {answers.length > 0 && (
        <>
          <div className="ld-sub" style={{ margin: "14px 0 6px", fontWeight: 700 }}>Form answers</div>
          <dl className="ld-kv">{answers.map(([k, v]) => <React.Fragment key={k}><dt>{label(k)}</dt><dd>{v || "—"}</dd></React.Fragment>)}</dl>
        </>
      )}
    </div>
  );
};

// ─── Update lead: status, call / WhatsApp summary and follow-up, in one ─────
// The only place to record a conversation. One save = one timeline entry.
const UpdateForm = ({ lead, meta, demoSessions, onDone }) => {
  const [status, setStatus] = useState(lead.status);
  const [channel, setChannel] = useState("call");
  const [summary, setSummary] = useState("");
  const current = toLocalInput(lead.followUpAt);
  const [followUp, setFollowUp] = useState(current);
  const [clearing, setClearing] = useState(false);
  const [demoSessionId, setDemoSessionId] = useState(lead.demoSessionId || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const def = meta.statuses.find((s) => s.key === status);

  const statusChanged = status !== lead.status;
  const final = FINAL.includes(status);
  const askFollowUp = !final && !(statusChanged && status === "ready_for_demo");
  const followUpChanged = clearing || (askFollowUp && followUp !== current);
  const demoChanged = status === "ready_for_demo" && demoSessionId && demoSessionId !== String(lead.demoSessionId || "");
  const dirty = statusChanged || summary.trim() || followUpChanged || demoChanged;
  const label = statusChanged ? `Change to "${def?.label}"` : summary.trim() ? `Save ${channel === "whatsapp" ? "WhatsApp" : "call"} summary` : followUpChanged ? (clearing ? "Clear follow-up" : "Save follow-up") : demoChanged ? "Save demo session" : "Save";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await leadApi.logUpdate(lead._id, {
        status, channel, summary,
        ...(clearing ? { clearFollowUp: true } : askFollowUp && followUp ? { followUpAt: fromLocalInput(followUp) } : {}),
        demoSessionId: status === "ready_for_demo" ? demoSessionId : undefined,
      });
      onDone(statusChanged ? `Status changed to ${def.label}.` : summary.trim() ? "Summary saved." : clearing ? "Follow-up cleared." : "Saved.");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="ld-card ld-update" onSubmit={submit}>
      <h3 className="ld-card-title">Update lead</h3>
      <p className="ld-sub" style={{ marginTop: -6 }}>After a call or WhatsApp chat: change the status if needed, note what was said and set the next follow-up.</p>
      {error && <div className="ld-alert ld-alert-error">{error}</div>}

      <label className="ld-field">
        <span>Status</span>
        <select className="ld-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          {meta.statuses.map((s) => <option key={s.key} value={s.key}>{s.label}{s.key === lead.status ? " (current)" : ""}</option>)}
        </select>
        {statusChanged && def?.hint && <span className="ld-field-hint">{def.hint}</span>}
      </label>
      {status === "ready_for_demo" && (
        <label className="ld-field">
          <span>Demo session<em>*</em></span>
          <select className="ld-select" value={demoSessionId} onChange={(e) => setDemoSessionId(e.target.value)} required>
            <option value="">Choose a session…</option>
            {demoSessions.map((s) => <option key={s._id} value={s._id}>{fmtDateTime(s.startsAt)}{s.title ? ` — ${s.title}` : ""}</option>)}
          </select>
          {!demoSessions.length && <span className="ld-field-hint">No upcoming sessions for this course — add one in <Link to="/leads/setup?tab=demos">Lead settings → Demo sessions</Link>.</span>}
        </label>
      )}

      <div className="ld-field">
        <span>Summary{statusChanged && <em>*</em>}</span>
        <div className="ld-seg" role="radiogroup" aria-label="Conversation">
          {[["call", "📞 Call"], ["whatsapp", "💬 WhatsApp"]].map(([k, text]) => (
            <button key={k} type="button" role="radio" aria-checked={channel === k} className={channel === k ? "is-on" : ""} onClick={() => setChannel(k)}>{text}</button>
          ))}
        </div>
        <textarea className="ld-textarea" value={summary} onChange={(e) => setSummary(e.target.value)} required={statusChanged}
          placeholder={statusChanged ? "What was discussed? Needed to change the status." : "What was discussed? (optional — skip it to only set a follow-up)"} />
        <span className="ld-field-hint">The date, time and your name are recorded automatically.</span>
      </div>

      {askFollowUp && (
        <div className="ld-field">
          <span>Next follow-up{def?.followUp && <em>*</em>}</span>
          {lead.followUpAt && !clearing && <span className="ld-field-hint">Now: {fmtDateTime(lead.followUpAt)} ({fmtDue(lead.followUpAt)})</span>}
          {clearing ? (
            <div className="ld-badges"><span className="ld-sub">The follow-up will be cleared.</span><button type="button" className="ld-link" onClick={() => setClearing(false)}>Undo</button></div>
          ) : (
            <>
              <FollowUpInput value={followUp} onChange={setFollowUp} required={def?.followUp} />
              {lead.followUpAt && !def?.followUp && <button type="button" className="ld-link" style={{ alignSelf: "flex-start" }} onClick={() => setClearing(true)}>Clear follow-up</button>}
            </>
          )}
        </div>
      )}
      {final && lead.followUpAt && <div className="ld-field-hint" style={{ marginBottom: 10 }}>A final status clears the follow-up and stops the automated messages.</div>}

      <button type="submit" className="ld-btn ld-btn-primary" disabled={busy || !dirty} style={{ width: "100%" }}>{busy ? "Saving…" : label}</button>
    </form>
  );
};

// ─── WhatsApp: custom message and payment link ────────────────────────────────
const MessagesCard = ({ lead, detail, meta, onDone }) => {
  const [customId, setCustomId] = useState("");
  const custom = detail.customMessages.find((m) => String(m._id) === customId);
  const [channels, setChannels] = useState({ whatsapp: true, email: false });
  const chosen = custom ? ["whatsapp", "email"].filter((c) => channels[c] && custom[c]) : [];
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const run = async (key, fn, ok) => {
    setBusy(key);
    setError("");
    try { await fn(); onDone(ok); } catch (e) { setError(errorText(e)); } finally { setBusy(""); }
  };
  const p = lead.payment || {};
  return (
    <div className="ld-card">
      <h3 className="ld-card-title">Messages</h3>
      {error && <div className="ld-alert ld-alert-error">{error}</div>}
      <label className="ld-field">
        <span>Send a custom message</span>
        <select className="ld-select" value={customId} onChange={(e) => setCustomId(e.target.value)}>
          <option value="">Choose a message…</option>
          {detail.customMessages.map((m) => <option key={m._id} value={m._id} disabled={!m.hasTemplate}>{m.title}{m.hasTemplate ? ` (${[m.whatsapp && "WhatsApp", m.email && "email"].filter(Boolean).join(" + ")})` : " (no template set)"}</option>)}
        </select>
        {!detail.customMessages.length && <span className="ld-field-hint">No custom messages for this course yet — add them in <Link to="/leads/setup?tab=automation">Lead settings</Link>.</span>}
      </label>
      {custom && (
        <div className="ld-badges" style={{ marginBottom: 10 }}>
          {[["whatsapp", "WhatsApp", lead.mobile], ["email", "Email", lead.email]].map(([k, name, to]) => (
            <label key={k} className="ld-chip" style={{ cursor: custom[k] && to ? "pointer" : "not-allowed", opacity: custom[k] && to ? 1 : 0.5 }} title={!custom[k] ? `No ${name.toLowerCase()} template set for this message` : !to ? `The lead has no ${k === "email" ? "email address" : "mobile number"}` : ""}>
              <input type="checkbox" checked={!!channels[k] && !!custom[k] && !!to} disabled={!custom[k] || !to} onChange={(e) => setChannels((c) => ({ ...c, [k]: e.target.checked }))} /> {name}
            </label>
          ))}
        </div>
      )}
      <button type="button" className="ld-btn" style={{ width: "100%" }} disabled={!chosen.length || busy}
        onClick={() => window.confirm(`Send "${custom.title}" to the lead now by ${chosen.map((c) => (c === "email" ? "email" : "WhatsApp")).join(" and ")}?`) && run("custom", () => leadApi.sendCustom(lead._id, customId, chosen), "Message sent.")}>
        {busy === "custom" ? "Sending…" : "Send message"}
      </button>

      <div style={{ borderTop: "1px solid #e2e8f0", margin: "16px 0 12px" }} />
      <div className="ld-field"><span>Registration fee — payment link</span>
        {p.shortUrl && (
          <div className="ld-sub">
            {p.status === "paid" ? `Paid ₹${Number(p.amount).toLocaleString("en-IN")} on ${fmtDateTime(p.paidAt)}` : `₹${Number(p.amount).toLocaleString("en-IN")} · ${p.status || "sent"} · sent ${fmtDateTime(p.sentAt)}`}
            {" · "}<a href={p.shortUrl} target="_blank" rel="noreferrer">{p.shortUrl}</a>
          </div>
        )}
      </div>
      {p.status !== "paid" && (
        <>
          <div className="ld-grid-2">
            <label className="ld-field"><span>Amount (₹)</span>
              <input className="ld-input" type="number" min={meta.paymentMin} max={meta.paymentMax} step="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`${meta.paymentMin}–${meta.paymentMax}`} />
            </label>
            <label className="ld-field"><span>Description</span><input className="ld-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Registration fee" /></label>
          </div>
          {!detail.paymentMessageReady && <div className="ld-field-hint" style={{ marginBottom: 8 }}>Set the payment-link template for this course first (<Link to="/leads/setup?tab=automation">Lead settings</Link>).</div>}
          <button type="button" className="ld-btn ld-btn-primary" style={{ width: "100%" }}
            disabled={!amount || busy || !detail.paymentMessageReady}
            onClick={() => window.confirm(`Create a Razorpay link for ₹${Number(amount).toLocaleString("en-IN")} and send it on WhatsApp?${p.status === "created" ? " The previous link will be cancelled." : ""}`)
              && run("pay", () => leadApi.sendPaymentLink(lead._id, { amount: Number(amount), description }), "Payment link sent.")}>
            {busy === "pay" ? "Creating…" : p.shortUrl ? "Send a new payment link" : "Send payment link"}
          </button>
          <div className="ld-field-hint" style={{ marginTop: 6 }}>Valid 7 days. When paid, the lead becomes Registered and the course details are sent automatically.</div>
        </>
      )}
    </div>
  );
};

// ─── The page ─────────────────────────────────────────────────────────────────
const LeadDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const meta = useLeadMeta();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState("timeline");
  // "Refresh status" in Notification history: null | { busy, tone, text }.
  const [refreshing, setRefreshing] = useState(null);

  // Previous / Next through the list this lead was opened from: its filters
  // ride along in this page's address (?statuses=…&sort=…), set by the list.
  const { search } = useLocation();
  const listQuery = search.replace(/^\?/, "");
  const listLink = `/leads/list${search}`;
  const leadLink = (leadId) => `/leads/${leadId}${search}`;
  const [neighbors, setNeighbors] = useState(null);
  // Stepping to another lead starts it fresh: no open edit form or message from the last one.
  useEffect(() => { setEditing(false); setNotice(""); setError(""); window.scrollTo(0, 0); }, [id]);
  useEffect(() => {
    setNeighbors(null);
    leadApi.neighbors(id, listQuery).then(setNeighbors).catch(() => setNeighbors(null));
  }, [id, listQuery]);

  const load = useCallback(() => leadApi.get(id).then(setDetail).catch((e) => setError(errorText(e))), [id]);
  useEffect(() => { load(); }, [load]);
  const done = (msg) => { setNotice(msg); load(); setTimeout(() => setNotice(""), 4000); };

  if (error && !detail) return <div className="ld-page"><div className="ld-alert ld-alert-error">{error}</div><Link to={listLink} className="ld-btn">Back to leads</Link></div>;
  if (!detail || !meta) return <div className="ld-page"><div className="ld-empty">Loading lead…</div></div>;

  const { lead, activities, messages } = detail;
  const overdue = lead.followUpAt && isOverdue(lead.followUpAt) && !FINAL.includes(lead.status);
  const last = activities[0];

  const toggleAutomation = async () => {
    try { await leadApi.setAutomation(lead._id, !lead.automationOn); done(`Automation turned ${lead.automationOn ? "off" : "on"}.`); } catch (e) { setError(errorText(e)); }
  };

  // This lead's sent messages: their delivery as UraanWapp has it now.
  const refreshStatus = async () => {
    setRefreshing({ busy: true, tone: "info", text: "Checking delivery with UraanWapp…" });
    try {
      const t = await refreshUntilDone((since) => leadApi.refreshLeadMessages(id, since),
        (p) => setRefreshing({ busy: true, tone: "info", text: `Checking delivery… ${p.checked} checked${p.remaining ? `, ${p.remaining} left` : ""}` }));
      await load();
      setRefreshing({ tone: "success", text: t.checked
        ? `Delivery checked for ${t.checked} message${t.checked === 1 ? "" : "s"}: ${t.changed} changed.`
        : "Every sent message already has its final status (read, failed) or nothing to check." });
    } catch (e) {
      setRefreshing({ tone: "error", text: `Refresh stopped: ${errorText(e)}` });
    }
  };

  const resend = async (m) => {
    if (!window.confirm(`Send "${m.title}" again?`)) return;
    try { await leadApi.resendMessage(m._id); done("Message sent again."); } catch (e) { setError(errorText(e)); }
  };

  const details = [
    ["Mobile", lead.mobile ? <a href={`tel:+91${lead.mobile}`}>{lead.mobile}</a> : "—"],
    ["Alternative number", lead.altMobile ? <a href={`tel:+91${lead.altMobile}`}>{lead.altMobile}</a> : "—"],
    ["Email", lead.email ? <a href={`mailto:${lead.email}`}>{lead.email}</a> : "—"],
    ["Education", [lead.education, lead.educationCourse].filter(Boolean).join(" — ") || "—"],
    ["Address", lead.address || "—"],
    ["City / State", [lead.city, lead.state].filter(Boolean).join(", ") || "—"],
    ["Course", lead.courseTitle],
    ["Campaign", lead.campaignName],
    ["Source", sourceLabel(lead.source)],
    ...(lead.stage ? [["Stage", lead.stage]] : []),
    ["Demo session", lead.demoSession ? `${fmtDateTime(lead.demoSession.startsAt)}${lead.demoSession.title ? ` — ${lead.demoSession.title}` : ""}${lead.demoSession.isCancelled ? " (cancelled)" : ""}` : lead.meta?.leadgenId ? "Unassigned" : "—"],
    ["Created", fmtDateTime(lead.createdAt)],
    ["Form submissions", lead.submissions || 1],
  ];

  return (
    <div className="ld-page">
      <div className="ld-head">
        <div>
          <div className="ld-lead-nav">
            <Link to={listLink} className="ld-link">← Leads</Link>
            {neighbors && (
              <span className="ld-lead-nav-steps">
                <button type="button" className="ld-btn ld-btn-sm" disabled={!neighbors.prev} title={neighbors.prev ? `Previous: ${neighbors.prev.fullName}` : "This is the first lead"} onClick={() => navigate(leadLink(neighbors.prev._id))}>‹ Previous</button>
                <span className="ld-sub" title={neighbors.position ? "" : "This lead no longer matches the list's filters"}>
                  {neighbors.position ? `${neighbors.position.toLocaleString("en-IN")} of ${neighbors.total.toLocaleString("en-IN")}` : `${neighbors.total.toLocaleString("en-IN")} in the list`}
                </span>
                <button type="button" className="ld-btn ld-btn-sm" disabled={!neighbors.next} title={neighbors.next ? `Next: ${neighbors.next.fullName}` : "This is the last lead"} onClick={() => navigate(leadLink(neighbors.next._id))}>Next ›</button>
              </span>
            )}
          </div>
          <h1 className="ld-title" style={{ marginTop: 6 }}>{lead.fullName}</h1>
          <div className="ld-badges">
            <StatusPill status={lead.status} label={lead.statusLabel} group={lead.statusGroup} />
            <span className="ld-sub">{lead.courseTitle} · {lead.campaignName}</span>
          </div>
        </div>
        <div className="ld-head-actions">
          <label className="ld-toggle" title="Day-wise messages and demo reminders">
            <input type="checkbox" checked={lead.automationOn} onChange={toggleAutomation} disabled={FINAL.includes(lead.status)} />
            <span className="ld-toggle-track" /> Automation {lead.automationOn ? "on" : "off"}
          </label>
          <button type="button" className="ld-btn" onClick={() => setEditing(true)}>Edit details</button>
          <button type="button" className="ld-btn ld-btn-danger" onClick={async () => {
            if (!window.confirm("Delete this lead? Its scheduled messages are cancelled.")) return;
            await leadApi.remove(lead._id);
            // On to the next lead in the list (or the previous), else back to the list.
            const goTo = neighbors?.next || neighbors?.prev;
            navigate(goTo ? leadLink(goTo._id) : listLink);
          }}>Delete</button>
        </div>
      </div>

      {notice && <div className="ld-alert ld-alert-ok">{notice}</div>}
      {error && <div className="ld-alert ld-alert-error">{error}</div>}

      {last && (
        <div className="ld-last">
          <div style={{ flex: 1 }}>
            <div className="ld-last-label">Last activity</div>
            <div className="ld-last-text">{last.summary}</div>
            <div className="ld-sub">{fmtDateTime(last.at)} · {last.by}</div>
          </div>
          {lead.followUpAt && (
            <span className={`ld-chip${overdue ? " is-overdue" : ""}`}>Follow-up {fmtDue(lead.followUpAt)} · {fmtDateTime(lead.followUpAt)}</span>
          )}
        </div>
      )}

      <div className="ld-detail-grid">
        <div>
          <div className="ld-card">
            <h3 className="ld-card-title">Lead details</h3>
            <dl className="ld-kv">{details.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}</dl>
          </div>

          {lead.meta?.leadgenId && <MetaCard meta={lead.meta} />}

          <div className="ld-card">
            <div className="ld-tabs">
              <button type="button" className={`ld-tab${tab === "timeline" ? " is-active" : ""}`} onClick={() => setTab("timeline")}>Timeline ({activities.length})</button>
              <button type="button" className={`ld-tab${tab === "messages" ? " is-active" : ""}`} onClick={() => setTab("messages")}>Notification history ({messages.length})</button>
            </div>
            {tab === "timeline" ? (
              <ul className="ld-timeline">
                {activities.map((a, i) => (
                  <li key={a._id} className={`ld-tl-item${i === 0 ? " is-latest" : ""}`} data-type={a.type}>
                    <span className="ld-tl-dot" />
                    <div className="ld-tl-body">
                      <div className="ld-tl-text">{a.summary}</div>
                      <div className="ld-tl-meta">{fmtDateTime(a.at)} · {a.by}{a.followUpAt ? ` · follow-up ${fmtDateTime(a.followUpAt)}` : ""}</div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="ld-table-wrap">
                <div className="ld-tab-tools">
                  <span className={refreshing ? `ld-alert ld-alert-${refreshing.tone}` : "ld-sub"} style={refreshing ? { margin: 0, padding: "6px 10px", display: "inline-flex", alignItems: "center", gap: 8 } : undefined}>
                    {refreshing?.busy && <span className="ld-spinner" aria-hidden="true" />}
                    {refreshing ? refreshing.text : "Delivery is checked with UraanWapp every few minutes."}
                  </span>
                  <button type="button" className="ld-btn ld-btn-sm" disabled={refreshing?.busy || !messages.length} onClick={refreshStatus} title="Ask UraanWapp now for the delivery of this lead's sent messages">↻ Refresh status</button>
                </div>
                <table className="ld-table">
                  <thead><tr><th>Channel</th><th>When</th><th>Message</th><th>Status</th><th /></tr></thead>
                  <tbody>
                    {messages.map((m) => (
                      <tr key={m._id} className={`ld-msg-row is-${m.channel}`} style={{ cursor: "default" }}>
                        <td><ChannelBadge channel={m.channel} /></td>
                        <td className="ld-sub" style={{ whiteSpace: "nowrap" }}>{fmtDateTime(m.sentAt || m.dueAt)}<div>{m.status === "scheduled" || m.status === "paused" ? "scheduled" : ""}</div></td>
                        <td>
                          <div className="ld-name">{m.title}</div>
                          <div className="ld-sub">{KIND[m.kind] || m.kind}{m.templateName ? ` · ${m.templateName}` : ""}{m.attachment?.fileName ? ` · 📎 ${m.attachment.fileName}` : ""}</div>
                          {m.preview && <details><summary className="ld-sub" style={{ cursor: "pointer" }}>Show message</summary><div className="ld-msg-preview">{m.preview}</div></details>}
                          {(m.error || m.skipReason) && <div className="ld-field-error" style={{ color: m.status === "failed" ? undefined : "#64748b" }}>{m.error || m.skipReason}</div>}
                        </td>
                        <td>
                          <span className="ld-msg-status" data-s={m.status}>{m.status}</span>
                          {m.deliveryStatus && m.status === "sent" && (
                            <div className="ld-msg-status" data-s={m.deliveryStatus === "failed" ? "failed" : m.deliveryStatus === "queued" ? "scheduled" : "sent"} title={m.deliveryFailure || ""}>
                              {m.deliveryStatus === "unknown" ? "" : m.deliveryStatus}{m.deliveryFailure ? ` (${m.deliveryFailure})` : ""}
                            </div>
                          )}
                        </td>
                        <td>
                          {["failed", "sent", "skipped"].includes(m.status) && <button type="button" className="ld-btn ld-btn-sm" onClick={() => resend(m)}>Resend</button>}
                          {m.resentAt && <div className="ld-sub" style={{ marginTop: 4 }}>↻ Resent {fmtDateTime(m.resentAt)}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!messages.length && <div className="ld-empty">No messages yet.</div>}
              </div>
            )}
          </div>
        </div>

        <div>
          <UpdateForm key={`${lead._id}-${lead.status}-${activities.length}`} lead={lead} meta={meta} demoSessions={detail.demoSessions} onDone={done} />
          <MessagesCard lead={lead} detail={detail} meta={meta} onDone={done} />
        </div>
      </div>

      {/* The same Previous / Next, pinned to the bottom of the screen while the page scrolls. */}
      {neighbors && (neighbors.prev || neighbors.next) && (
        <nav className="ld-stepbar" aria-label="Previous and next lead">
          <button type="button" className="ld-stepbar-btn" disabled={!neighbors.prev} onClick={() => navigate(leadLink(neighbors.prev._id))}>
            <span className="ld-stepbar-arrow">‹</span>
            <span><small>Previous</small><b>{neighbors.prev ? neighbors.prev.fullName : "This is the first lead"}</b></span>
          </button>
          <span className="ld-stepbar-pos" title={neighbors.position ? "" : "This lead no longer matches the list's filters"}>
            {neighbors.position ? `${neighbors.position.toLocaleString("en-IN")} of ${neighbors.total.toLocaleString("en-IN")}` : `${neighbors.total.toLocaleString("en-IN")} in the list`}
          </span>
          <button type="button" className="ld-stepbar-btn is-next" disabled={!neighbors.next} onClick={() => navigate(leadLink(neighbors.next._id))}>
            <span><small>Next</small><b>{neighbors.next ? neighbors.next.fullName : "This is the last lead"}</b></span>
            <span className="ld-stepbar-arrow">›</span>
          </button>
        </nav>
      )}

      {editing && (
        <div className="ld-overlay" onMouseDown={(e) => e.target === e.currentTarget && setEditing(false)}>
          <div className="ld-modal" role="dialog" aria-modal="true" aria-label="Edit lead">
            <div className="ld-modal-head"><h3>Edit lead</h3><button type="button" className="ld-btn ld-btn-sm" onClick={() => setEditing(false)}>Close</button></div>
            <div className="ld-modal-body">
              <LeadForm initial={lead} onCancel={() => setEditing(false)} onSubmit={async (form) => { await leadApi.update(lead._id, form); setEditing(false); done("Details saved."); }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadDetails;
